/**
 * What the operator is told about the fallback images they have entered.
 *
 * Every notice here reports and waits: each of these states is one a brand can legitimately be
 * halfway through arranging, so none of them stands between the operator and saving the document.
 */
import { t } from "../../../i18n";
import { FALLBACK_ORIENTATIONS, FallbackOrientation } from "./centralImagesDocument";

interface FallbackImagesNoticesProps {
  /** The orientation configured on its own, or `undefined` when both or neither are. */
  soleOrientation: FallbackOrientation | undefined;
  /** The orientations whose configured url is addressed over plain http. */
  insecure: FallbackOrientation[];
  /** The orientations whose configured url could not be loaded. */
  unreachable: FallbackOrientation[];
  /** The orientations whose image measured the shape of the other one. */
  contradicted: FallbackOrientation[];
}

/**
 * The copy is read at render rather than at module load: this file is evaluated while the bundle
 * starts, before i18next holds any resources to answer a lookup from.
 */
const ORIENTATION_COPY: Record<
  FallbackOrientation,
  { soleOrientation: () => string; insecure: () => string; unreachable: () => string; contradicted: () => string }
> = {
  landscape: {
    soleOrientation: () =>
      t(
        "enforced-thumbnail-style.fallback-landscape-only",
        undefined,
        "Only the landscape fallback image is set. Games with no image of their own keep showing the default artwork on portrait tiles.",
      ),
    insecure: () =>
      t(
        "enforced-thumbnail-style.fallback-landscape-insecure",
        undefined,
        "The landscape fallback image is addressed over http, which players' browsers refuse to load on a secure page. Landscape tiles show the default artwork until the address is an https one.",
      ),
    unreachable: () =>
      t(
        "enforced-thumbnail-style.fallback-landscape-unreachable",
        undefined,
        "The landscape fallback image could not be loaded from that address. Landscape tiles show the default artwork until the address serves an image.",
      ),
    contradicted: () =>
      t(
        "enforced-thumbnail-style.fallback-landscape-holds-portrait",
        undefined,
        "The landscape fallback image is taller than it is wide, so landscape tiles will crop away its top and bottom.",
      ),
  },
  portrait: {
    soleOrientation: () =>
      t(
        "enforced-thumbnail-style.fallback-portrait-only",
        undefined,
        "Only the portrait fallback image is set. Games with no image of their own keep showing the default artwork on landscape tiles.",
      ),
    insecure: () =>
      t(
        "enforced-thumbnail-style.fallback-portrait-insecure",
        undefined,
        "The portrait fallback image is addressed over http, which players' browsers refuse to load on a secure page. Portrait tiles show the default artwork until the address is an https one.",
      ),
    unreachable: () =>
      t(
        "enforced-thumbnail-style.fallback-portrait-unreachable",
        undefined,
        "The portrait fallback image could not be loaded from that address. Portrait tiles show the default artwork until the address serves an image.",
      ),
    contradicted: () =>
      t(
        "enforced-thumbnail-style.fallback-portrait-holds-landscape",
        undefined,
        "The portrait fallback image is wider than it is tall, so portrait tiles will crop away its sides.",
      ),
  },
};

interface WarningProps {
  testId: string;
  children: string;
}

function Warning({ testId, children }: WarningProps): JSX.Element {
  return (
    <div
      className="alert alert-warning"
      role="alert"
      data-testid={testId}
    >
      {children}
    </div>
  );
}

/** In the orientations' own order rather than the order they were entered, so the notices hold still as the operator types. */
function inOrientationOrder(orientations: FallbackOrientation[]): FallbackOrientation[] {
  return FALLBACK_ORIENTATIONS.filter((orientation) => orientations.includes(orientation));
}

// The notices are ordered by when they can first be raised, not by how urgent they are: the scheme
// is known from the text on screen, a failed reading only once the document records the address. A
// list that grows downward leaves a notice the operator is already reading where they found it.
export function FallbackImagesNotices({ soleOrientation, insecure, unreachable, contradicted }: FallbackImagesNoticesProps): JSX.Element {
  return (
    <>
      {soleOrientation !== undefined && <Warning testId="fallback-images-single-orientation">{ORIENTATION_COPY[soleOrientation].soleOrientation()}</Warning>}

      {inOrientationOrder(insecure).map((orientation) => (
        <Warning
          key={orientation}
          testId={`fallback-images-insecure-${orientation}`}
        >
          {ORIENTATION_COPY[orientation].insecure()}
        </Warning>
      ))}

      {inOrientationOrder(unreachable).map((orientation) => (
        <Warning
          key={orientation}
          testId={`fallback-images-unreachable-${orientation}`}
        >
          {ORIENTATION_COPY[orientation].unreachable()}
        </Warning>
      ))}

      {inOrientationOrder(contradicted).map((orientation) => (
        <Warning
          key={orientation}
          testId={`fallback-images-orientation-contradicted-${orientation}`}
        >
          {ORIENTATION_COPY[orientation].contradicted()}
        </Warning>
      ))}
    </>
  );
}
